export type DeviceCalendarPermissionState =
  | 'unavailable'
  | 'undetermined'
  | 'denied'
  | 'granted';

export type DeviceCalendarOption = {
  id: string;
  title: string;
  sourceName: string;
  color?: string;
  allowsModifications: boolean;
  isPrimary: boolean;
};

export type ExternalDeviceCalendarEvent = {
  id: string;
  calendarId: string;
  calendarTitle: string;
  calendarColor?: string;
  title: string;
  startDate: Date;
  endDate: Date;
  location?: string;
  externalIdentity?: string;
  originalStartDate?: Date;
  allDay: boolean;
  recurring: boolean;
  availability?: 'free' | 'busy' | 'tentative' | 'unavailable';
  responseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  timeZone?: string;
  busy?: boolean;
};

export function normaliseDeviceCalendars(
  calendars: DeviceCalendarOption[]
): DeviceCalendarOption[] {
  const unique = new Map<string, DeviceCalendarOption>();
  for (const calendar of calendars) {
    if (!calendar.id || unique.has(calendar.id)) continue;
    unique.set(calendar.id, calendar);
  }
  return [...unique.values()].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    const sourceOrder = a.sourceName.localeCompare(b.sourceName);
    return sourceOrder || a.title.localeCompare(b.title);
  });
}

// Takvim kimlikleri cihaz/hesap kaldırıldığında geçersizleşebilir. Yalnızca o
// anda sistemde bulunan değerleri geri yükleyerek hayalet seçim üretmeyiz.
export function availableSelectedCalendarIds(
  savedIds: string[],
  calendars: DeviceCalendarOption[]
): string[] {
  const available = new Set(calendars.map((calendar) => calendar.id));
  return [...new Set(savedIds)].filter((id) => available.has(id));
}

export function intervalOverlapsDay(start: Date, end: Date, day: Date): boolean {
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
    return false;
  }
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const dayEnd = new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate() + 1);
  return start < dayEnd && end > dayStart;
}

export function eventOverlapsDay(event: ExternalDeviceCalendarEvent, day: Date): boolean {
  return intervalOverlapsDay(event.startDate, event.endDate, day);
}

// Sağlayıcı kaydını değiştirmeden yalnız görünen tek oluşumu gizlemek için
// takvim, olay ve oluşum başlangıcını birlikte kullanırız. Yinelenen serinin
// diğer örnekleri böylece görünür kalır.
export function externalCalendarEventVisibilityKey(
  event: ExternalDeviceCalendarEvent
): string {
  return [event.calendarId, event.id, event.startDate.toISOString()].join('\u0000');
}

function normalisedFingerprintText(value?: string): string {
  return value?.trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-US') ?? '';
}

function strongIdentityFingerprint(event: ExternalDeviceCalendarEvent): string | null {
  const identity = event.externalIdentity?.trim();
  if (!identity) return null;
  const occurrence = event.recurring
    ? (event.originalStartDate ?? event.startDate).toISOString()
    : 'single';
  return ['identity', identity, occurrence].join('\u0000');
}

function visibleFingerprint(event: ExternalDeviceCalendarEvent): string {
  return [
    'visible',
    event.allDay ? 'all-day' : 'timed',
    event.startDate.toISOString(),
    event.endDate.toISOString(),
    normalisedFingerprintText(event.title),
    normalisedFingerprintText(event.location),
  ].join('\u0000');
}

export function isDirectGoogleCalendarEvent(event: ExternalDeviceCalendarEvent): boolean {
  return event.calendarId.startsWith('google:');
}

/**
 * Aynı dış etkinlik cihaz sağlayıcısı ve doğrudan sağlayıcı bağlantısından
 * birlikte gelirse önce kalıcı sağlayıcı kimliği + yinelenen oluşum kökünü,
 * bu kimlik yoksa görünür alanların tam eşleşmesini kullanır. Veri silmez;
 * doğrudan sağlayıcı kaydını yalnız sunum için tercih eder.
 */
export function deduplicateExternalCalendarEventsForDisplay(
  events: ExternalDeviceCalendarEvent[]
): ExternalDeviceCalendarEvent[] {
  const groups = new Map<string, {
    device: ExternalDeviceCalendarEvent[];
    google: ExternalDeviceCalendarEvent[];
  }>();
  for (const event of events) {
    const fingerprint = strongIdentityFingerprint(event) ?? visibleFingerprint(event);
    const group = groups.get(fingerprint) ?? { device: [], google: [] };
    (isDirectGoogleCalendarEvent(event) ? group.google : group.device).push(event);
    groups.set(fingerprint, group);
  }

  const unique: ExternalDeviceCalendarEvent[] = [];
  for (const group of groups.values()) {
    if (group.google.length === 0 || group.device.length === 0) {
      unique.push(...group.google, ...group.device);
      continue;
    }

    // Görünür alanları aynı cihaz ve doğrudan Google kayıtlarını çiftler halinde
    // birleştir; doğrudan kaydı tercih et. Aynı kaynakta gerçekten birden fazla
    // kayıt varsa en yüksek kaynak çokluğunu koru ve ayrı etkinlikleri kaybetme.
    unique.push(...group.google);
    if (group.device.length > group.google.length) {
      unique.push(...group.device.slice(group.google.length));
    }
  }
  return unique.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}
